/**
 * PopoverController - A Stimulus controller for creating floating popover elements
 *
 * This documentation was AI generated, and reviewed by a human.
 *
 * This controller uses Floating UI to create popover/tooltip elements that can be positioned
 * relative to a trigger element. It supports various placement options, delays, virtual
 * popovers that can cross parent boundaries, and automatic positioning updates.
 *
 * CONFIGURATION OPTIONS (via data attributes):
 * - data-placement: Position of popover relative to trigger (default: "top")
 *   Options: "top", "bottom", "left", "right", "top-start", "top-end", etc.
 * - data-mobile-placement: Different placement for mobile screens (≤768px)
 * - data-offset: Distance between trigger and popover in pixels (default: 8)
 * - data-delay: Delay in milliseconds before showing popover (default: 0)
 * - data-prevent-default: Prevent default event behavior (default: false)
 * - data-prevent-event-propagation: Stop event propagation (default: false)
 * - data-virtual: Create virtual popover that can cross parent boundaries (default: false)
 *
 * TARGETS:
 * - content: The popover content element
 *
 * ACTIONS:
 * - show: Display the popover
 * - hide: Hide the popover
 * - toggle: Toggle popover visibility
 *
 * EVENTS:
 * - popover-shown: Fired when popover is fully shown
 * - popover-hidden: Fired when popover is fully hidden
 *
 * BASIC USAGE:
 *
 * <div data-controller="popover">
 *   <button data-action="click->popover#show">Show Popover</button>
 *   <div data-popover-target="content" class="popover">
 *     <div class="popover-content">
 *       Popover content goes here
 *     </div>
 *   </div>
 * </div>
 *
 * TOGGLE USAGE:
 *
 * <div data-controller="popover">
 *   <button data-action="click->popover#toggle">Toggle Popover</button>
 *   <div data-popover-target="content" class="popover">
 *     <div class="popover-content">
 *       Popover content goes here
 *     </div>
 *   </div>
 * </div>
 *
 * WITH CONFIGURATION:
 *
 * <div data-controller="popover"
 *      data-placement="bottom-start"
 *      data-mobile-placement="top"
 *      data-offset="12"
 *      data-delay="500"
 *      data-virtual="true">
 *   <button data-action="click->popover#show">Show Popover</button>
 *   <div data-popover-target="content" class="popover">
 *     <div class="arrow"></div>
 *     <div class="popover-content">
 *       Popover with arrow and custom positioning
 *     </div>
 *   </div>
 * </div>
 *
 * HOVER USAGE:
 *
 * <div data-controller="popover">
 *   <button data-action="mouseenter->popover#show mouseleave->popover#hide">
 *     Hover for tooltip
 *   </button>
 *   <div data-popover-target="content" class="popover">
 *     <div class="popover-content">
 *       Tooltip content
 *     </div>
 *   </div>
 * </div>
 */

import { Controller } from "@hotwired/stimulus";
import {
  computePosition,
  shift,
  flip,
  offset,
  arrow,
  autoUpdate,
} from "@floating-ui/dom";

export default class extends Controller {
  static get targets() {
    return ["content"];
  }

  connect() {
    this.isOpen = false;
    this.boundUpdate = this.update.bind(this);
    this.boundBodyClickHandler = this.closeHandler.bind(this);
    this.boundEmitShownEvent = this.emitShownEvent.bind(this);
    this.boundEmitHiddenEvent = this.emitHiddenEvent.bind(this);

    // configuration
    this.placement = this.element.dataset.placement || "top";
    this.mobilePlacement = this.element.dataset.mobilePlacement;
    this.offset = this.element.dataset.offset
      ? Number(this.element.dataset.offset)
      : 8;
    this.preventDefault = this.element.dataset.preventDefault || false;
    this.preventEventPropagation =
      this.element.dataset.preventEventPropagation || false;
    this.virtual = this.element.dataset.virtual;
    this.delay = this.element.dataset.delay
      ? Number(this.element.dataset.delay)
      : 0;

    this.appearTimer = 0;
    this.popoverElement = this.contentTarget;
    this.arrowElement = this.contentTarget.querySelector(".arrow");

    // if virtual boolean is set on the controller element, clone the contentTarget, append
    // to the end of the body, and let this controller manage that instead of the original
    // (to be used when a popover MUST be able to cross parent element boundaries)
    if (this.virtual) {
      this.addVirtualPopover();
    }

    this.middleware = [flip(), shift(), offset(this.offset)];

    if (this.arrowElement) {
      this.middleware.push(arrow({ element: this.arrowElement }));
    }
  }

  update() {
    if (!this.popoverElement || !this.hasContentTarget) {
      return;
    }
    const currentPlacement =
      this.mobilePlacement && screen.width <= 768
        ? this.mobilePlacement
        : this.placement;

    computePosition(this.element, this.popoverElement, {
      placement: currentPlacement,
      middleware: this.middleware,
    }).then(({ x, y, placement, middlewareData }) => {
      Object.assign(this.popoverElement.style, {
        left: `${x}px`,
        top: `${y}px`,
      });

      if (middlewareData.arrow) {
        const { x: arrowX, y: arrowY } = middlewareData.arrow;

        const staticSide = {
          top: "bottom",
          right: "left",
          bottom: "top",
          left: "right",
        }[placement.split("-")[0]];

        Object.assign(this.arrowElement.style, {
          left: arrowX != null ? `${arrowX}px` : "",
          top: arrowY != null ? `${arrowY}px` : "",
          right: "",
          bottom: "",
          [staticSide]: "-5px",
        });
      }
    });
  }

  appear() {
    this.popoverElement.classList.add("popover-shown");
    this.isOpen = true;

    // set up auto position updates while floating element is shown
    this.clearAutoUpdates = autoUpdate(
      this.element,
      this.popoverElement,
      this.boundUpdate,
    );

    document
      .querySelector("body")
      .addEventListener("click", this.boundBodyClickHandler);
  }

  show(e) {
    if (e && this.preventDefault) {
      e.preventDefault();
    }
    if (e && this.preventEventPropagation) {
      e.stopPropagation();
    }
    clearTimeout(this.appearTimer);
    this.clearPopoverTransitionListeners();

    if (this.delay) {
      this.appearTimer = setTimeout(this.appear.bind(this), this.delay);
    } else {
      this.appear();
    }

    this.popoverElement.addEventListener(
      "transitionend",
      this.boundEmitShownEvent,
      { once: true },
    );
  }

  hide(e) {
    if (e && this.preventDefault) {
      e.preventDefault();
    }
    if (e && this.preventEventPropagation) {
      console.log("popover: event propagation prevented");
      e.stopPropagation();
    }
    clearTimeout(this.appearTimer);
    this.clearPopoverTransitionListeners();

    this.popoverElement.classList.remove("popover-shown");
    this.isOpen = false;

    this.cleanup();

    this.popoverElement.addEventListener(
      "transitionend",
      this.boundEmitHiddenEvent,
      {
        once: true,
      },
    );
  }

  toggle(e) {
    this.isOpen ? this.hide(e) : this.show(e);
  }

  // closes floating element if event occured outside of it or the source element
  closeHandler(e) {
    if (!this.isOpen) {
      return;
    }

    if (this.popoverElement.contains(e.target)) {
      return;
    }
    if (this.element.contains(e.target)) {
      return;
    }

    this.hide();
  }

  cleanup() {
    if (this.clearAutoUpdates) {
      this.clearAutoUpdates();
      this.clearAutoUpdates = null;
    }

    document
      .querySelector("body")
      .removeEventListener("click", this.boundBodyClickHandler);
  }

  disconnect() {
    this.cleanup();
  }

  addVirtualPopover() {
    this.virtualPopover = document
      .querySelector("body")
      .appendChild(this.contentTarget.cloneNode(true));
    this.popoverElement = this.virtualPopover;
  }

  removeVirtualPopover() {
    if (!this.virtualPopover) {
      return;
    }

    this.virtualPopover.remove();
    this.virtualPopover = null;
    this.popoverElement = this.hasContentTarget ? this.contentTarget : null;
  }

  contentTargetConnected(_contentTarget) {
    if (this.virtual) {
      // clean up other virtual popovers, if present
      if (this.virtualPopover) {
        this.removeVirtualPopover();
      }

      this.popoverElement = this.contentTarget;
      this.addVirtualPopover();
    }

    if (this.isOpen) {
      this.show();
    }
  }

  contentTargetDisconnected(_contentTarget) {
    this.cleanup();

    if (this.virtual) {
      this.removeVirtualPopover();
    }
  }

  emitShownEvent() {
    this.popoverElement.dispatchEvent(new Event("popover-shown"));
  }

  emitHiddenEvent() {
    this.popoverElement.dispatchEvent(new Event("popover-hidden"));
  }

  clearPopoverTransitionListeners() {
    this.popoverElement.removeEventListener(
      "transitionend",
      this.boundEmitHiddenEvent,
    );
    this.popoverElement.removeEventListener(
      "transitionend",
      this.boundEmitShownEvent,
    );
  }
}
